import { useEffect, useState, useCallback } from 'react';
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom';
import { offers } from '@/api/endpoints';
import type { Offer, OfferStatus } from '@/types';
import { Icon } from '@/components/Icon';
import { useSortable, SortHeader } from '@/components/SortableTable';
import { Pagination, usePaginated } from '@/components/Pagination';
import { useAuth } from '@/auth/AuthContext';
import { formatMoney } from '@/lib/money';

/** Format ISO timestamp → 'DD.MM.YYYY HH:mm' (pl). Pusta gdy null. */
function fmtDateTime(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('pl-PL', { dateStyle: 'short', timeStyle: 'short' }).format(d);
}

const FILTERS: { value: OfferStatus | ''; label: string }[] = [
  { value: '', label: 'Wszystkie' },
  { value: 'DRAFT', label: 'Szkice' },
  { value: 'SENT', label: 'Wysłane' },
  { value: 'ACCEPTED', label: 'Zaakceptowane' },
  { value: 'ORDERED', label: 'Zlecenia' },
  { value: 'REJECTED', label: 'Odrzucone' },
  { value: 'EXPIRED', label: 'Wygasłe' },
];

const STATUS_COLOR: Record<OfferStatus, string> = {
  DRAFT: 'bg-ink-100 text-ink-600',
  SENT: 'bg-water-100 text-water-700',
  ACCEPTED: 'bg-green-100 text-green-700',
  ORDERED: 'bg-emerald-100 text-emerald-700',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-amber-100 text-amber-700',
  CONVERTED: 'bg-purple-100 text-purple-700',
};

export function OffersList() {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as OfferStatus | '';
  const [rows, setRows] = useState<Offer[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const nav = useNavigate();
  const { user } = useAuth();
  const canCreateOffers = user?.permissions?.includes('OFFER_CREATE') ?? false;

  useEffect(() => {
    setRows(null);
    setErr(null);
    offers.list(status || undefined)
      .then(setRows)
      .catch(e => setErr(e?.message ?? String(e)));
  }, [status]);

  const setStatus = (s: string) => {
    const next = new URLSearchParams(params);
    if (s) next.set('status', s); else next.delete('status');
    setParams(next, { replace: true });
  };

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Oferty</h1>
          <div className="text-ink-400 text-[13px]">Wyceny prac dla klientów</div>
        </div>
        {canCreateOffers && (
          <NavLink to="/oferta-wizard" className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> Nowa oferta
          </NavLink>
        )}
      </header>

      <div className="card">
        <div className="card-head">
          <div className="flex gap-1 bg-ink-50 p-1 rounded-md">
            {FILTERS.map(f => (
              <button
                key={f.value || 'all'}
                onClick={() => setStatus(f.value)}
                className={'btn btn-sm ' + (status === f.value ? 'btn-primary' : 'btn-ghost')}
              >{f.label}</button>
            ))}
          </div>
        </div>
        <div className="card-body !p-0">
          {err && <div className="text-red-500 p-4">Błąd: {err}</div>}
          {!rows && !err && <div className="text-ink-400 p-4">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="text-ink-400 p-6 text-center">
              {status ? 'Brak ofert w wybranym statusie.' : 'Brak ofert. Utwórz pierwszą.'}
            </div>
          )}
          {rows && rows.length > 0 && <OffersTable rows={rows} onRowClick={id => nav(`/oferty/${id}`)}/>}
        </div>
      </div>
    </div>
  );
}

/** „Ujęcie: Siucice, studnie: nr 1, nr 2” — miejscowość zamiast pełnej nazwy ujęcia.
 *  Oferta z wybranymi studniami pokazuje tylko je, inaczej wszystkie studnie ujęć. */
function intakeLine(o: Offer): string {
  const intakes = o.intakes ?? [];
  const towns = [...new Set(intakes.map(u => u.town?.trim() || u.name).filter(Boolean))];
  if (towns.length === 0 && o.intakeName) towns.push(o.intakeName);
  const wells = o.boreholes?.length ? o.boreholes.map(b => b.designation)
    : o.boreholeDesignation ? [o.boreholeDesignation] : [...new Set(intakes.flatMap(u => u.wells ?? []))];
  const nr = (d: string) => (/^\d/.test(d) ? `nr ${d}` : d);
  const parts: string[] = [];
  if (towns.length) parts.push(`Ujęcie: ${towns.join(', ')}`);
  if (wells.length) parts.push(`${wells.length === 1 ? 'studnia' : 'studnie'}: ${wells.map(nr).join(', ')}`);
  return parts.join(', ');
}

type SortKey = 'number' | 'title' | 'city' | 'client' | 'issueDate' | 'validUntil' | 'totalGross' | 'status' | 'createdAt';

function OffersTable({ rows, onRowClick }: { rows: Offer[]; onRowClick: (id: number) => void }) {
  const accessor = useCallback((o: Offer, k: SortKey) => {
    switch (k) {
      case 'number': return o.number;
      case 'title': return o.title;
      case 'city': return o.client.city ?? null;
      case 'client': return o.client.name;
      case 'issueDate': return o.issueDate ? new Date(o.issueDate) : null;
      case 'validUntil': return o.validUntil ? new Date(o.validUntil) : null;
      case 'totalGross': return Number(o.totalGross);
      case 'status': return o.statusName;
      case 'createdAt': return o.createdAt ? new Date(o.createdAt) : null;
    }
  }, []);
  const { sorted, sort, onHeaderClick } = useSortable<Offer, SortKey>(rows, 'createdAt', 'desc', accessor);
  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(sorted, { storageKey: 'offers' });

  return (
    <>
    <table className="table-hw">
      <thead>
        <tr>
          <SortHeader<SortKey> label="Numer"            sortKey="number"     current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Tytuł"            sortKey="title"      current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Siedziba"         sortKey="city"       current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Klient"           sortKey="client"     current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Wystawiona"       sortKey="issueDate"  current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Ważna do"         sortKey="validUntil" current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Wartość brutto"   sortKey="totalGross" current={sort} onClick={onHeaderClick} className="text-right" align="right"/>
          <SortHeader<SortKey> label="Status"           sortKey="status"     current={sort} onClick={onHeaderClick}/>
          <SortHeader<SortKey> label="Utworzona"        sortKey="createdAt"  current={sort} onClick={onHeaderClick}/>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {pagedItems.map(o => (
          <tr key={o.id} onClick={() => onRowClick(o.id)} className="cursor-pointer hover:bg-ink-50">
            <td className="font-mono text-[12px] text-ink-500">{o.number}</td>
            <td>
              <div className="font-semibold text-ink-900">{o.title}</div>
              {intakeLine(o) && <div className="text-[11px] text-ink-400">{intakeLine(o)}</div>}
            </td>
            <td className="text-ink-700 text-[12px]">{o.client.city || '—'}</td>
            <td className="text-ink-700 text-[12px]">{o.client.name}</td>
            <td className="text-ink-500 text-[12px]">{o.issueDate}</td>
            <td className="text-ink-500 text-[12px]">{o.validUntil ?? '—'}</td>
            <td className="text-right font-mono tabular-nums text-ink-800">{formatMoney(o.totalGross)} zł</td>
            <td>
              <span className={'inline-block px-2 py-0.5 rounded text-[11px] font-semibold ' + STATUS_COLOR[o.status]}>
                {o.statusName}
              </span>
            </td>
            <td className="text-ink-500 text-[12px] font-mono whitespace-nowrap">{fmtDateTime(o.createdAt)}</td>
            <td><Icon name="chevron" className="text-ink-300"/></td>
          </tr>
        ))}
      </tbody>
    </table>
    <Pagination total={total} page={page} pageSize={pageSize}
                onPageChange={setPage} onPageSizeChange={setPageSize}/>
    </>
  );
}
